import { useContext, useEffect, useRef, useState } from "react";
import { CircleHelp, CircleX, LoaderCircle, RefreshCw, Search, TriangleAlert, Unlink2 } from "lucide-react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Heading from "../../../shared_components/Heading/Heading";
import PaginationGroup from "../../../shared_components/PaginationGroup/PaginationGroup";
import UserData from "../../../services/UserData";
import IngestionService from "../../../services/IngestionService";
import NotFound from "../../NotFound/NotFound";
import style from "./IngestionBatches.module.css";

const statuses = {
  DISCOVERING: { Icon: Search, className: style.discovering },
  PROCESSING: { Icon: LoaderCircle, className: style.processing },
  INTERRUPTED: { Icon: Unlink2, className: style.failed },
  PARTIAL: { Icon: TriangleAlert, className: style.partial },
  FAILED: { Icon: CircleX, className: style.failed },
};

const StatusIcon = ({ status }) => {
  const { Icon, className } = statuses[status] || { Icon: CircleHelp };
  const tooltip = status === "INTERRUPTED" ? "Interrupted — will not be reprocessed" : status || "Unknown status";
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
      <span tabIndex={0} role="img" aria-label={status || "Unknown status"} className={`${style.status} ${className || ""}`}>
        <Icon size={22} aria-hidden="true" />
      </span>
    </OverlayTrigger>
  );
};

const getCount = (batch, count) => {
  const kind = batch.ingestion_type === "TEAMWORK_COMMENT" ? "comment" : "file";
  return (batch[`${count}_${kind}_count`] ?? 0).toLocaleString();
};

const BatchDateTime = ({ value }) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  const formattedDate = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return <time dateTime={value} title={value}>{formattedDate}, {date.toLocaleTimeString()}</time>;
};

const IngestionBatches = () => {
  const userData = useContext(UserData);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [paused, setPaused] = useState(document.hidden);
  const refresh = useRef(() => {});

  useEffect(() => {
    let active = true;
    let inFlight = false;
    let nextRefreshAt = Date.now() + 30000;
    setLoading(true);
    setError(null);
    if (!userData) return () => { active = false; };

    const loadBatches = async () => {
      if (!active || inFlight || document.hidden) return;
      inFlight = true;
      setLoading(true);
      setError(null);
      try {
        const result = await IngestionService.getBatches(userData, page, pageSize);
        if (active) setData(result);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        inFlight = false;
        if (active) {
          setLoading(false);
          nextRefreshAt = Date.now() + 30000;
          setCountdown(30);
        }
      }
    };
    refresh.current = loadBatches;
    loadBatches();
    const interval = setInterval(() => {
      if (document.hidden || inFlight) return;
      const remaining = Math.max(0, Math.ceil((nextRefreshAt - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) loadBatches();
    }, 1000);
    const onVisibilityChange = () => {
      setPaused(document.hidden);
      if (!document.hidden) loadBatches();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      refresh.current = () => {};
    };
  }, [userData, page, pageSize]);

  return (
    <section className={style.container} aria-label="Ingestion Batches" aria-busy={loading}>
      <div className={style.header}>
        <Heading>Ingestion Batches</Heading>
        <div className={style.refreshControls}>
          <span className={style.countdown} role="timer" aria-label="Automatic refresh">
            {paused ? "Auto-refresh paused" : loading ? "Refreshing…" : `Refresh in ${countdown}s`}
          </span>
          <button
          type="button"
          className={`btn btn-outline-secondary ${style.refresh}`}
          onClick={() => refresh.current()}
          disabled={loading || !userData}
          title="Refresh ingestion batches"
          aria-label="Refresh ingestion batches"
        >
          <RefreshCw size={20} aria-hidden="true" />
          Refresh
        </button>
        </div>
      </div>
      <div className={style.loadingSlot}>
        {loading && <div className={style.loadingBar} role="progressbar" aria-label="Loading ingestion batches" />}
      </div>
      <div className={style.content}>
        {error ? (
          <NotFound />
        ) : data && (
          <table className={style.table}>
            <thead>
              <tr>
                <th scope="col">Ingestion type</th>
                <th scope="col">Discovered count</th>
                <th scope="col">Processed count</th>
                <th scope="col">Failed count</th>
                <th scope="col">Time started</th>
                <th scope="col">Last update</th>
              </tr>
            </thead>
            <tbody>
              {data.content.map(batch => (
                <tr key={batch.id}>
                  <td><div className={style.type}><StatusIcon status={batch.status} />{batch.ingestion_type}</div></td>
                  <td>{getCount(batch, "discovered")}</td>
                  <td>{getCount(batch, "ingested")}</td>
                  <td>{getCount(batch, "failed")}</td>
                  <td><BatchDateTime value={batch.started_at} /></td>
                  <td><BatchDateTime value={batch.updated_at} /></td>
                </tr>
              ))}
              {data.content.length === 0 && <tr><td colSpan={6}>No ingestion batches found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {!error && data && (
        <div className={style.pagination}>
          <PaginationGroup activePage={page} numberOfPages={Math.max(1, data.total_pages)} updatePageClicked={setPage} />
          <label className={style.pageSize}>
            Page size
            <select
              className="form-select form-select-sm"
              value={pageSize}
              onChange={event => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        </div>
      )}
    </section>
  );
};

export default IngestionBatches;
